import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { JwtHelperService } from '@auth0/angular-jwt'
import { Router } from '@angular/router';
import { alert } from 'devextreme/ui/dialog';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from "@sentry/angular";

@Injectable()
export class AuthService {
  tokenURL = environment.API_TOKEN_URL;
  serviceUrl = environment.API_URL + 'userlog';
  refreshTokenTimeout: any;

  constructor(private _jwtHelper: JwtHelperService, private httpClient: HttpClient, private router: Router,
    private _translateService: TranslateService
  ) {
  }

  userHasClaim(claimName: string): boolean {
    let userClaims: String[] = this.getUserClaims();
    if (!!userClaims) {
      return (userClaims.indexOf(claimName) > -1);
    }
    return false;
  }

  public signInAs(userId: string) {
    var self = this;

    const body = new URLSearchParams();
    body.append('refresh_token', localStorage.getItem(environment.DEFAULT_REFRESH_TOKEN_NAME));
    body.append('grant_type', 'refresh_token');
    body.append('OnBehalfOfId', userId);

    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const options = { headers: headers };

    return this.httpClient.post(this.tokenURL, body, options).subscribe(function (token: any) {
      localStorage.setItem(environment.DEFAULT_TOKEN_NAME, token.access_token);
      localStorage.setItem(environment.DEFAULT_REFRESH_TOKEN_NAME, token.refresh_token);
      localStorage.setItem('ActiveCustomerId', token.activeCustomerId);
      self.router.navigateByUrl(self.getStartPageUrl()).then(() => window.location.reload());
    }, error => (error) => {
      alert('Het is niet mogelijk om in te loggen als deze gebruiker, log opnieuw in en probeer het nogmaals of neem contact op met de beheerder', 'Er is iets fout gegaan').then(() => {
        self.logout();
        self.router.navigate(['auth', 'login']);
      });
    });
  }

  refreshTokens(activeCustomerId: number): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const options = { headers: headers };
    const body = new URLSearchParams();
    body.append('refresh_token', localStorage.getItem(environment.DEFAULT_REFRESH_TOKEN_NAME));
    body.append('grant_type', 'refresh_token');
    body.append('activeCustomerId', `${activeCustomerId}`);
    return this.httpClient.post(this.tokenURL, body, options);
  }

  refreshCurrentToken(): any {
    const activeCustomerId = this.getActiveCustomerId();
    const activeDepartmentId = this.getActiveDepartmentId();
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const options = { headers: headers };
    const body = new URLSearchParams();
    body.append('refresh_token', localStorage.getItem(environment.DEFAULT_REFRESH_TOKEN_NAME));
    body.append('grant_type', 'refresh_token');
    body.append('activeCustomerId', `${activeCustomerId}`);
    body.append('activeDepartmentId', `${activeDepartmentId}`);
    this.httpClient.post(this.tokenURL, body, options).subscribe((token: any) => {
      localStorage.setItem(environment.DEFAULT_TOKEN_NAME, token.access_token);
      localStorage.setItem(environment.DEFAULT_REFRESH_TOKEN_NAME, token.refresh_token);
      this.startRefreshTokenTimer();
    });
  }

  changeActiveDepartment(activeCustomerId: number, activeDepartmentId: number): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const options = { headers: headers };
    const body = new URLSearchParams();
    body.append('refresh_token', localStorage.getItem(environment.DEFAULT_REFRESH_TOKEN_NAME));
    body.append('grant_type', 'refresh_token');
    body.append('activeCustomerId', `${activeCustomerId}`);
    body.append('activeDepartmentId', `${activeDepartmentId}`);
    return this.httpClient.post(this.tokenURL, body, options);
  }

  public startRefreshTokenTimer() {
    const decodedToken = this.getDecodedToken();
    const expires = new Date(decodedToken.exp * 1000);
    const timeout = expires.getTime() - Date.now() - (60 * 1000);
    this.refreshTokenTimeout = setTimeout(() => this.refreshCurrentToken(), timeout);
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }

  userHasRole(roles: String[]): boolean {
    if (roles.length == 0) return true;
    let userRoles: String[] = this.getUserRoles();
    let found: boolean = false;
    if (!!userRoles == true) {
      for (var i = 0; i < userRoles.length; i++) {
        let element = userRoles[i];

        for (var y = 0; y < roles.length; y++) {
          if (element.toUpperCase() == roles[y].toUpperCase()) {
            found = true;
            break;
          }
        }
        if (found) break;
      };
    }
    return found;
  }

  userHasCustomerRole(roles: String[]): boolean {
    if (roles.length == 0) return true;
    let userRoles: String[] = this.getUserCustomerRoles();
    let found: boolean = false;
    if (!!userRoles == true) {
      for (var i = 0; i < userRoles.length; i++) {
        let element = userRoles[i];

        for (var y = 0; y < roles.length; y++) {
          if (element.toUpperCase() == roles[y].toUpperCase()) {
            found = true;
            break;
          }
        }
        if (found) break;
      };
    }
    return found;
  }

  getUserCustomerRoles(): String[] {
    let token: string = localStorage.getItem(environment.DEFAULT_TOKEN_NAME);
    let roles = this._jwtHelper.decodeToken(token).customerRole;

    if (roles == null) return [];

    if (!Array.isArray(roles)) {
      roles = [roles];
    }

    return roles || [];

  }

  getUserRoles(): String[] {
    let token: string = localStorage.getItem(environment.DEFAULT_TOKEN_NAME);
    let roles = this._jwtHelper.decodeToken(token).role;

    if (roles == null) return [];

    if (!Array.isArray(roles)) {
      roles = [roles];
    }

    return roles || [];
  }

  getStartPageUrl(): string {
    let startPageUrl = '/pages/accessdenied';
    const token: string = localStorage.getItem(environment.DEFAULT_TOKEN_NAME);
    if (!!token) {
      //rights for TotalCare
      if (Number(this._jwtHelper.decodeToken(token).CustomerType) == 1) {
        if (this.userHasRole(['DashboardPresenter'])) {
          startPageUrl = '/presenter/dashboard'
        } else if (this.userHasCustomerRole(['Administrator', 'Beheerder', 'Klantbeheerder', 'Gebruiker'])) {
          startPageUrl = '/pages/dashboard';
        } else if (this.userHasCustomerRole(['Seveso'])) {
          startPageUrl = '/pages/seveso/dashboard'
        }
      }

      //only rights for the BRZO module
      if (Number(this._jwtHelper.decodeToken(token).CustomerType) == 2) {
        startPageUrl = '/pages/seveso/dashboard';
      }
    }

    return startPageUrl;
  }

  getSubscriptionExpiresInDays(): number {
    let subscriptionExpiresInDays = 0;
    const token: string = localStorage.getItem(environment.DEFAULT_TOKEN_NAME);
    if (!!token) subscriptionExpiresInDays = Number(this._jwtHelper.decodeToken(token).AantalDagenAbonnement);
    return subscriptionExpiresInDays;
  }

  getSubscriptionExpiryDate(): any {
    let subscriptionExpiryDate = null;
    const token: string = localStorage.getItem(environment.DEFAULT_TOKEN_NAME);
    if (!!token) subscriptionExpiryDate = this._jwtHelper.decodeToken(token).EindDatumTotalCare;
    return subscriptionExpiryDate;
  }

  getUserClaims(): String[] {
    let claims: String[];
    const token: string = localStorage.getItem(environment.DEFAULT_TOKEN_NAME);
    if (!!token) claims = this._jwtHelper.decodeToken(token).userClaims;
    return claims || [];
  }

  getUserId(): string {
    const token: string = localStorage.getItem(environment.DEFAULT_TOKEN_NAME);
    const decodedToken = this._jwtHelper.decodeToken(token);
    return decodedToken['sub'];
  }

  getUserName(): string {
    const token: string = localStorage.getItem(environment.DEFAULT_TOKEN_NAME);
    const decodedToken = this._jwtHelper.decodeToken(token);
    return decodedToken['name'];
  }

  getName(): string {
    const token: string = localStorage.getItem(environment.DEFAULT_TOKEN_NAME);
    const decodedToken = this._jwtHelper.decodeToken(token);
    return decodedToken['name'];
  }

  getEmail(): string {
    const token: string = localStorage.getItem(environment.DEFAULT_TOKEN_NAME);
    const decodedToken = this._jwtHelper.decodeToken(token);
    return decodedToken['email'];
  }

  getDecodedToken(): any {
    const token: string = localStorage.getItem(environment.DEFAULT_TOKEN_NAME);
    return this._jwtHelper.decodeToken(token);
  }

  getToken(): string {
    const token: string = localStorage.getItem(environment.DEFAULT_TOKEN_NAME);
    return token;
  }

  getActiveDepartmentId(): string {
    const decodedToken = this.getDecodedToken();
    return decodedToken['ActiveAfdelingId'] ?? '';
  }

  getActiveCustomerId(): string {
    const decodedToken = this.getDecodedToken();
    return decodedToken['ActiveKlantId'] ?? '';
  }

  logout(): void {
    this.stopRefreshTokenTimer();
    localStorage.removeItem(environment.DEFAULT_TOKEN_NAME);
    localStorage.removeItem(environment.DEFAULT_REFRESH_TOKEN_NAME);
    Sentry.setUser(null);
  }

  logLogon(): Observable<any> {
    return this.httpClient.post(this.serviceUrl + '/LogLogon', {});
  }

  logFailedLogon(): void { }

  getCulterByLanguage() {
    const cultures = [
      { lng: 'nl', culture: 'nl-NL' },
      { lng: 'de', culture: 'de-DE' },
      { lng: 'en', culture: 'en-US' },
    ]

    return cultures.find(x => x.lng == this._translateService.currentLang).culture || 'nl-NL';
  }

  getOnderhoudsmodusActief(): Observable<boolean> {
    return this.httpClient.get<boolean>(environment.API_URL + 'status/OnderhoudsmodusActief');
  }
}
